/*
 * Cosmetic countdown for the cart drawer.
 *
 * Deliberately isolated: it reads nothing from the cart, writes nothing to
 * it, and blocks nothing. At zero it stops and leaves the message in place.
 * Checkout availability is entirely Shopify's business and this file never
 * touches it.
 *
 * The drawer is re-rendered by section rendering on every cart change, which
 * replaces the element, so the timer is attached by observation rather than
 * once on load. Each element keeps its own deadline in sessionStorage so a
 * re-render does not restart the count.
 */
(() => {
  // Renamed from 'pb-cart-timer-deadline' so a deadline left by the old
  // 12-minute urgency timer is not picked up.
  const KEY = 'pb-cart-reserved-deadline';
  const tick = new Map();

  const format = (ms) => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const deadlineFor = (minutes) => {
    let stored = null;
    try {
      stored = sessionStorage.getItem(KEY);
    } catch {
      // Private browsing, or storage blocked. Fall through to a fresh one.
    }

    // A stored deadline further away than the current setting (the minutes
    // were lowered in the editor) is discarded rather than honoured.
    const span = minutes * 60 * 1000;
    const parsed = stored ? parseInt(stored, 10) : NaN;
    if (Number.isFinite(parsed) && parsed > Date.now() && parsed - Date.now() <= span) return parsed;

    const next = Date.now() + span;
    try {
      sessionStorage.setItem(KEY, String(next));
    } catch {
      // Not storable; the timer still runs for this render.
    }
    return next;
  };

  const start = (el) => {
    if (tick.has(el)) return;
    const clock = el.querySelector('[data-pb-cart-clock]');
    if (!clock) return;

    const minutes = parseInt(el.dataset.minutes, 10) || 10;
    const deadline = deadlineFor(minutes);

    const update = () => {
      const left = deadline - Date.now();
      clock.textContent = format(left);
      if (left <= 0) {
        clearInterval(tick.get(el));
        tick.delete(el);
      }
    };

    update();
    tick.set(el, setInterval(update, 1000));
  };

  const scan = (root) => {
    if (!root || root.nodeType !== 1) return;
    if (root.matches?.('[data-pb-cart-timer]')) start(root);
    root.querySelectorAll?.('[data-pb-cart-timer]').forEach(start);
  };

  const sweep = () => {
    // Drop intervals whose element has been replaced by a re-render.
    tick.forEach((id, el) => {
      if (!el.isConnected) {
        clearInterval(id);
        tick.delete(el);
      }
    });
  };

  const observe = () => {
    scan(document.body);
    new MutationObserver((records) => {
      sweep();
      for (const record of records) {
        record.addedNodes.forEach(scan);
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe, { once: true });
  } else {
    observe();
  }
})();
